<template>
  <div
    ref="wrapper "
    :class="[wrapperClasses, { active: visible }]"
  >
    <label class="dropdown-label">
      {{ label }}
    </label>

    <div
      :class="classes"
      :data-value="modelValue"
      :data-list="options"
    >
      <div
        class="selector"
        :class="{ expanded: visible }"
        @click="toggle()"
      >
        <div class="selected">
          <span v-if="modelValue">
            {{ displayModel(modelValue) }}
          </span>
          <span v-else>
            {{ placeholder }}
          </span>
        </div>
        <Icon
          v-if="!hideArrow"
          :icon="(modelValue && showClearButtonWhenSelected) ? 'x' : 'chevron-down'"
          class="arrow"
          :class="{ expanded: visible }"
          :size="pillStyle ? '10px' : '18px'"
          @click="iconClicked"
        />
        <transition
          name="dropdown-transition"
          @before-enter="beforeEnter"
          @enter="enter"
          @before-leave="beforeLeave"
          @leave="leave"
        >
          <ul
            v-show="visible"
            class="dropdown-menu"
            :style="'max-height:' + listHeight + 'px'"
          >
            <li
              v-for="item in options"
              :key="item?.toString()"
              :class="{ current: item === modelValue }"
              @click="select(item)"
            >
              {{ displayModel(item) }}
            </li>
          </ul>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import {computed, defineComponent, reactive, Transition} from 'vue';
import Icon from "@/javascript/views/components/Icon.vue";

export default defineComponent({
  name: 'DropdownSelect',
  components: {
    Icon,
    Transition,
  },
  model: {
    prop: 'modelValue',
    event: 'change',
  },

  props: {
    /**
     * List of options to display
     * @type {Array}
     */
    options: {
      type: Array,
      // eslint-disable-next-line vue/require-valid-default-prop
      default: [],
    },
    /**
     * Model Value to update when selected
     */
    // eslint-disable-next-line vue/require-prop-types
    modelValue: {
      default: '',
    },
    /**
     * Field value to render when passing an object as v-model
     * @type {string}
     */
    fieldValue: {
      type: String,
      default: null,
    },
    /**
     * Placeholder text when no option selected
     * @type {string}
     */
    placeholder: {
      type: String,
      default: 'Select...',
    },
    /**
     * Formatted label to describe dropdown
     * @type {string}
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Number of items to display before scrolling starts. Set to 0 or null for full length list
     * @type {Number}
     */
    itemsToDisplay: {
      type: Number,
      default: 5,
    },
    /**
     * Should expand to take up available space?
     * @type {Boolean}
     */
    compact: {
      type: Boolean,
      default: true,
    },
    /**
     * Removes button from document flow to prevent list from getting cut off in some elements.
     * Only needed when overflow is hidden in parent element.
     * @type {Boolean}
     */
    floating: {
      type: Boolean,
      default: false,
    },
    /**
     * What type of background the button is on: light, dark
     * @type {String}
     */
    on: {
      type: String,
      default: 'light',
    },
    /**
     * Hides the arrow indicator
     * @type {Boolean}
     */
    hideArrow: {
      type: Boolean,
      default: false
    },
    /**
     * For some different styling
     * Makes int a pill, much smaller, rounded corners
     */
    pillStyle: {
      type: Boolean,
      default: false
    },

    /**
     * Changes the dropdown arrow to an x when an option is selected to allow clearing of the selection
     */
    showClearButtonWhenSelected: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'select'],
  setup(props) {
    props = reactive(props);
    return {
      classes: computed(() => ({
        'dropdown-select': true,
        'dropdown--expanded': !props.compact,
        'dropdown--pill-style': props.pillStyle
      })),
      wrapperClasses: computed(() => ({
        'dropdown-select-wrapper': true,
        floating: props.floating,
        'dropdown-wrapper--on-dark': props.on === 'dark',
      })),
    };
  },
  data() {
    return {
      selectedOption: '',
      visible: false,
    };
  },
  computed: {
    listHeight() {
      return this.itemsToDisplay && this.itemsToDisplay > 0
        ? 66 * Math.min(this.options.length, this.itemsToDisplay) + 20
        : 'inherit';
    },
  },
  mounted() {
    document.addEventListener('click', this.dismiss);
  },
  methods: {
    toggle() {
      this.visible = !this.visible;
    },
    // When the icon is clicked and it's the 'x' button, clear the current selection
    // And stop the dropdown opening
    iconClicked(event) {
      if(this.modelValue && this.showClearButtonWhenSelected) {
        event.stopPropagation();
        this.select(null);
      }
    },
    dismiss(event) {
      const element = this.$refs.wrapper;
      if (element && !element.contains(event.target)) {
        // Click is outside the element, call your function
        if (this.visible) this.visible = false;
      }
    },
    displayModel(value) {
      if (this.fieldValue) {
        return value[this.fieldValue];
      } else return value;
    },
    select(option) {
      this.$emit('select', option);
      this.$emit('update:modelValue', option);
    },
    //Animation functions
    beforeEnter: function (el) {
      el.style.height = '0';
    },
    enter: function (el) {
      el.style.height = el.scrollHeight + 'px';
    },
    beforeLeave: function (el) {
      el.style.height = '0';
    },
    leave: function (el) {
      el.style.height = '0';
    },
  },
});
</script>
<style scoped lang="scss">
/**************************************************************************************
 * Base styles for the DropdownSelect component that are common across design systems *
 * Variables are declared in the specific components                                  *
 **************************************************************************************/

.dropdown-select-wrapper {
  display: block;
  position: relative;
  cursor: pointer !important;

  &.dropdown-wrapper--on-dark {
    .dropdown-label {
      color: black;
    }
  }
}


.dropdown-select {
  width: 208px;
  position: absolute;

  &.dropdown--expanded {
    width: 100%;
  }

  .selector {
    background: transparent;
    position: relative;
    z-index: 2;
    box-sizing: border-box;

    &.expanded {
      transition: 550ms ease-out;
    }

    .arrow {
      position: absolute;
      right: 10px;
      top: 16px;
      transform: rotateZ(0deg) translateY(0px);
      transition-duration: 0.3s;
      transition-timing-function: cubic-bezier(.59, 1.39, .37, 1.01);

      &.expanded {
        transform: rotateZ(180deg) translateY(2px);
      }
    }

    .selected {
      display: block;
      padding: 12px 20px 12px 12px;
      margin-right: 20px;
      font-size: 16px;
      color: black;
      border-bottom: 1px solid gray;
      text-transform: capitalize !important;
      width: 100%;

    }
  }

  .dropdown-menu {
    @apply shadow-xl;
    transition: 250ms linear;
    overflow: auto;
    width: 100%;
    list-style-type: none;
    padding: 0;
    margin: 0;
    font-size: 16px;
    box-sizing: border-box;
    left: -1px;
    z-index: 1;
    background: white;
    border-radius: 0 0 4px 4px !important;
    cursor: pointer;
    border: 1px solid gray;
    border-top: none;

    /* HIDE SCROLLBAR */
    -ms-overflow-style: none; // Internet Explorer 10+
    scrollbar-width: none; // Firefox

    &.collapsed {
      border-bottom: none;
    }

    &::-webkit-scrollbar {
      display: none; // Safari and Chrome
    }

    //border:2px solid teal !important;
  }

  li {
    padding: 9px;
    margin: 0 8px;
    border-bottom: 1px solid gray;
    border-radius: 0;
    @apply text-gray-500;
    text-transform: capitalize !important;

    &:last-child {
      border-bottom: none;
    }


    &:hover {
      @apply border-b-default_primary_dark;
      @apply text-default_primary_dark;
      @apply bg-default_primary_lightest;
    }
  }

  .current {
    @apply border-b-default_primary_dark;
    @apply text-default_primary_dark;
  }
}

.dropdown--pill-style {

  &.dropdown-select {
    width: unset;
  }
  .selector {

    .selected {
      white-space: nowrap;
      font-size: 12px;
      padding: 4px 16px 4px 8px;
      border: 1px solid gray;
      background-color: whitesmoke;
      border-radius: 14px;


      .arrow {
        position: absolute;

        &.expanded {
          transform: rotateZ(180deg) translateY(2px);
        }
      }
    }
    &.expanded {
      .selected {
        border-radius: 12px 12px 0 0;
      }
    }

    .arrow {
      right: 8px;
      top: 8px;
    }
    .dropdown-menu {
      font-size: 12px;
      li {
        margin: 0;

      }
    }
    font-size: 5px !important;
  }
}
</style>
